/**
 * Состояние краша и соединение с сервером.
 *
 * Множитель здесь не хранится: он вычисляется из времени старта на каждом кадре
 * анимации. Держать его в состоянии значило бы перерисовывать всё приложение
 * шестьдесят раз в секунду.
 *
 * Часы клиента могут отличаться от серверных, поэтому при подключении
 * замеряется расхождение и дальше все расчёты идут в серверном времени.
 */

import { create } from "zustand";

import {
  CrashPhase,
  type CrashBetDto,
  type CrashClientMessage,
  type CrashHistoryItem,
  type CrashRoundDto,
  type CrashServerMessage,
} from "@koshkodrop/shared";

import { tokens } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "./session";

type Connection = "idle" | "connecting" | "open" | "closed";

interface CrashState {
  connection: Connection;
  round: CrashRoundDto | null;
  history: CrashHistoryItem[];
  /** Разница серверных и клиентских часов в миллисекундах. */
  clockSkew: number;
  /** Ставка текущего игрока в этом раунде. */
  myBet: CrashBetDto | null;
  lastError: { code: string; message: string } | null;
  /** Результат прошлого раунда для всплывающего уведомления. */
  lastResult: { multiplier: number; payout: string } | null;
  /** Когда откроется следующий раунд после краша. */
  nextRoundStartsAt: number | null;

  connect: () => void;
  disconnect: () => void;
  placeBet: (stake: bigint, autoCashoutAt: number | null) => void;
  cashout: () => void;
  clearError: () => void;
  clearResult: () => void;
  /** Текущее серверное время по нашим часам с поправкой. */
  serverNow: () => number;
}

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let pingTimer: number | null = null;
let reconnectAttempt = 0;

export const useCrash = create<CrashState>((set, get) => {
  const send = (message: CrashClientMessage) => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  };

  const scheduleReconnect = () => {
    if (reconnectTimer !== null) return;
    // Нарастающая задержка: при падении сервера сотни клиентов не должны
    // штурмовать его одновременно
    const delay = Math.min(1000 * 2 ** reconnectAttempt, 15_000);
    reconnectAttempt += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      get().connect();
    }, delay);
  };

  const handleMessage = (message: CrashServerMessage) => {
    switch (message.type) {
      case "state": {
        const myId = useSession.getState().user?.id;
        set({
          round: message.round,
          history: message.history,
          clockSkew: message.serverTime - Date.now(),
          myBet: message.round.bets.find((bet) => bet.userId === myId) ?? null,
        });
        break;
      }

      case "betting": {
        set({
          round: message.round,
          clockSkew: message.serverTime - Date.now(),
          myBet: null,
          lastResult: null,
          nextRoundStartsAt: null,
        });
        break;
      }

      case "running": {
        const round = get().round;
        if (!round || round.id !== message.roundId) break;
        set({
          round: { ...round, phase: CrashPhase.Running, startedAt: message.startedAt },
          clockSkew: message.serverTime - Date.now(),
        });
        break;
      }

      case "crashed": {
        const round = get().round;
        const myId = useSession.getState().user?.id;
        const myBetBefore = get().myBet;

        if (myBetBefore && myBetBefore.status === "active") {
          haptic.error();
        }

        const settledBets =
          message.bets.length > 0
            ? message.bets
            : (round?.bets ?? []).map((bet) =>
                bet.status === "active" ? { ...bet, status: "lost" as const, payout: "0" } : bet,
              );

        const myBet =
          settledBets.find((bet) => bet.userId === myId) ??
          (myBetBefore && myBetBefore.status === "active"
            ? { ...myBetBefore, status: "lost" as const, payout: "0" }
            : myBetBefore);

        set({
          round: round
            ? {
                ...round,
                phase: CrashPhase.Crashed,
                crashPoint: message.crashPoint,
                serverSeed: message.serverSeed,
                bets: settledBets,
              }
            : round,
          clockSkew: message.serverTime - Date.now(),
          nextRoundStartsAt: message.nextRoundStartsAt,
          // Полосу истории обновляем сразу, не дожидаясь следующего состояния
          history: round
            ? [
                {
                  id: round.id,
                  sequence: round.sequence,
                  crashPoint: message.crashPoint,
                  commitHash: round.commitHash,
                  serverSeed: message.serverSeed,
                  crashedAt: new Date(message.serverTime).toISOString(),
                },
                ...get().history,
              ].slice(0, 30)
            : get().history,
          myBet,
        });
        break;
      }

      case "bet": {
        const round = get().round;
        if (!round || round.id !== message.roundId) break;
        const myId = useSession.getState().user?.id;
        const bets = [...round.bets.filter((bet) => bet.id !== message.bet.id), message.bet];
        set({
          round: { ...round, bets },
          myBet: message.bet.userId === myId ? message.bet : get().myBet,
        });
        break;
      }

      case "cashout": {
        const round = get().round;
        if (!round) break;
        const bets = round.bets.map((bet) =>
          bet.id === message.betId
            ? {
                ...bet,
                status: "cashed_out" as const,
                cashedOutAt: message.multiplier,
                payout: message.payout,
              }
            : bet,
        );
        const myBet = get().myBet;
        const isMine = myBet?.id === message.betId;

        if (isMine) haptic.success();

        set({
          round: { ...round, bets },
          myBet: isMine
            ? {
                ...myBet,
                status: "cashed_out",
                cashedOutAt: message.multiplier,
                payout: message.payout,
              }
            : myBet,
          lastResult: isMine
            ? { multiplier: message.multiplier, payout: message.payout }
            : get().lastResult,
        });
        break;
      }

      case "balance": {
        useSession.getState().setBalance(BigInt(message.balance));
        break;
      }

      case "error": {
        set({ lastError: { code: message.code, message: message.message } });
        haptic.error();
        break;
      }

      case "pong":
        set({ clockSkew: message.serverTime - Date.now() });
        break;
    }
  };

  return {
    connection: "idle",
    round: null,
    history: [],
    clockSkew: 0,
    myBet: null,
    lastError: null,
    lastResult: null,
    nextRoundStartsAt: null,

    connect() {
      if (socket && (socket.readyState === 0 || socket.readyState === 1)) return;

      set({ connection: "connecting" });

      const protocol = location.protocol === "https:" ? "wss:" : "ws:";
      const token = tokens.access;
      const url = `${protocol}//${location.host}/api/crash/ws${token ? `?token=${encodeURIComponent(token)}` : ""}`;

      socket = new WebSocket(url);

      socket.onopen = () => {
        reconnectAttempt = 0;
        set({ connection: "open" });
        // Пинг заодно переизмеряет расхождение часов
        pingTimer = window.setInterval(() => send({ type: "ping" }), 25_000);
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        try {
          handleMessage(JSON.parse(event.data) as CrashServerMessage);
        } catch {
          // Некорректное сообщение не должно рвать соединение
        }
      };

      socket.onclose = () => {
        if (pingTimer !== null) {
          clearInterval(pingTimer);
          pingTimer = null;
        }
        set({ connection: "closed" });
        scheduleReconnect();
      };

      socket.onerror = () => {
        socket?.close();
      };
    },

    disconnect() {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (pingTimer !== null) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      socket?.close();
      socket = null;
      set({ connection: "idle" });
    },

    placeBet(stake, autoCashoutAt) {
      haptic.tap("medium");
      send({ type: "bet", stake: stake.toString(), autoCashoutAt });
    },

    cashout() {
      haptic.tap("heavy");
      send({ type: "cashout" });
    },

    clearError() {
      set({ lastError: null });
    },

    clearResult() {
      set({ lastResult: null });
    },

    serverNow() {
      return Date.now() + get().clockSkew;
    },
  };
});
